import { useState, useRef, useEffect, useCallback } from 'react';
import {
  streamChat,
  listSessions,
  deleteSession,
  approvePendingWrite,
  declinePendingWrite,
  type ChatEvent,
  type ChatSession,
  type ScreenData,
} from '../api/aiChat';
import { PendingConfirmationCard, type PendingConfirmation } from './PendingConfirmationCard';

interface CopilotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  streaming?: boolean;
  statusText?: string;
  // Optional pending-write confirmation card (Privacy Policy §11).
  // Rendered with Approve/Decline buttons when state === 'pending'.
  pendingConfirmation?: PendingConfirmation;
}

export interface AiAction {
  actionType: 'navigate' | 'ui_click' | 'ui_fill' | 'activate_workspace';
  path?: string;
  reason: string;
  target?: string;
  value?: string;
  formData?: {
    fields?: Record<string, unknown>;
    lines?: unknown[];
    explanation?: string;
  };
}

export interface CopilotAction {
  tool: string;
  input: unknown;
  result: unknown;
}

export interface CopilotResult {
  text: string;
  actions: CopilotAction[];
  error?: string;
}

interface CopilotPanelProps {
  open: boolean;
  onClose: () => void;
  pageContext: string;
  screenData: ScreenData;
  onAction?: (action: AiAction) => void;
}

const COPILOT_STORAGE_KEY = 'a5core_copilot_open';
const COPILOT_MESSAGES_KEY = 'a5core_copilot_messages';
const COPILOT_CONVERSATION_KEY = 'a5core_copilot_conversation_id';
const SESSION_STORAGE_KEY = 'a5core_copilot_session_id';
const MAX_PERSISTED_MESSAGES = 100;

export function toggleCopilotStorage(open: boolean): void {
  localStorage.setItem(COPILOT_STORAGE_KEY, open ? '1' : '0');
}

// Track the completion callback for external callers (e.g., arena)
let _completionCallback: ((result: CopilotResult) => void) | null = null;

/**
 * Send a message to the copilot panel from external components (e.g. arena, AIFloatingBuddy).
 * Opens the copilot and dispatches the message text for the component to pick up.
 * Optional onComplete fires when the copilot finishes processing.
 */
export function sendCopilotMessage(message: string, onComplete?: (result: CopilotResult) => void): void {
  _completionCallback = onComplete ?? null;
  window.dispatchEvent(new CustomEvent('copilot-open'));
  window.dispatchEvent(new CustomEvent('copilot-send-message', { detail: message }));
}

export function getCopilotStorage(): boolean {
  return localStorage.getItem(COPILOT_STORAGE_KEY) === '1';
}

function saveMessagesToStorage(messages: CopilotMessage[], conversationId?: string): void {
  try {
    const toSave = messages.slice(-MAX_PERSISTED_MESSAGES).map(m => ({
      id: m.id,
      role: m.role,
      content: m.content,
      timestamp: m.timestamp.toISOString(),
    }));
    localStorage.setItem(COPILOT_MESSAGES_KEY, JSON.stringify(toSave));
    if (conversationId) {
      localStorage.setItem(COPILOT_CONVERSATION_KEY, conversationId);
    } else {
      localStorage.removeItem(COPILOT_CONVERSATION_KEY);
    }
  } catch { /* quota exceeded or private browsing — ignore */ }
}

function loadMessagesFromStorage(): { messages: CopilotMessage[], conversationId?: string } {
  try {
    const raw = localStorage.getItem(COPILOT_MESSAGES_KEY);
    const convId = localStorage.getItem(COPILOT_CONVERSATION_KEY) || undefined;
    if (!raw) return { messages: [], conversationId: convId };
    const parsed = JSON.parse(raw) as Array<{ id: string; role: 'user' | 'assistant'; content: string; timestamp: string }>;
    return {
      messages: parsed.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: new Date(m.timestamp),
      })),
      conversationId: convId,
    };
  } catch {
    return { messages: [], conversationId: undefined };
  }
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-0.5 ml-1 align-middle">
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '0.6s' }} />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms', animationDuration: '0.6s' }} />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms', animationDuration: '0.6s' }} />
    </span>
  );
}

function StreamingCursor() {
  return (
    <span className="inline-flex items-center gap-[2px] ml-0.5 align-middle">
      <span className="w-[3px] h-[3px] bg-gray-400 rounded-full animate-pulse" />
      <span className="w-[3px] h-[3px] bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '200ms' }} />
      <span className="w-[3px] h-[3px] bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '400ms' }} />
    </span>
  );
}

function formatMarkdown(text: string): string {
  let html = text
    .replace(/```(\w*)\n?([\s\S]*?)```/g, '<pre class="bg-gray-100 rounded p-2 text-xs overflow-x-auto my-1"><code>$2</code></pre>')
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-xs">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
  return html;
}

/**
 * Streaming bubble — writes directly to a DOM ref, bypassing React re-renders.
 * Only triggers React state for status/cursor changes, not for every text token.
 */
function StreamingBubble({ msgId }: { msgId: string }) {
  const contentRef = useRef<HTMLDivElement>(null);

  // Register this ref globally so the streaming handler can write to it
  useEffect(() => {
    streamingRefs.set(msgId, contentRef);
    return () => { streamingRefs.delete(msgId); };
  }, [msgId]);

  return (
    <div ref={contentRef} className="prose prose-sm max-w-none whitespace-pre-wrap" />
  );
}

// Global map: streaming message ID → its DOM ref. Used to write text directly.
const streamingRefs = new Map<string, React.RefObject<HTMLDivElement | null>>();

/**
 * Smooth streaming animation using a jitter-buffer pattern:
 *   1. Pre-buffer chars before starting display (absorbs API burst timing)
 *   2. Drain at a constant rate via requestAnimationFrame
 *   3. Accelerate drain when stream ends to avoid sluggish tail
 */
const PRE_BUFFER = 50;   // chars to accumulate before display starts
const RATE_NORMAL = 1;   // chars/frame during streaming  (60 chars/sec)
const RATE_CATCHUP = 2;  // chars/frame when buffer is deep (prevents runaway growth)
const RATE_DRAIN = 6;    // chars/frame after stream ends  (fast tail drain)
const CATCHUP_AT = 60;   // switch to catch-up rate above this buffer depth

const animState = {
  pending: '',
  displayed: '',
  rafId: 0,
  msgId: null as string | null,
  streamDone: false,
  started: false,
};

function animTick() {
  if (!animState.msgId) { animState.rafId = 0; return; }

  const pending = animState.pending;

  // Nothing to show right now
  if (pending.length === 0) {
    if (animState.streamDone) { animState.rafId = 0; return; } // done forever
    animState.rafId = requestAnimationFrame(animTick);
    return;
  }

  // Pre-buffering phase: wait until we have enough to sustain smooth playback
  if (!animState.started && !animState.streamDone) {
    if (pending.length < PRE_BUFFER) {
      animState.rafId = requestAnimationFrame(animTick);
      return;
    }
    animState.started = true;
  }

  // Choose rate
  let rate: number;
  if (animState.streamDone) {
    rate = RATE_DRAIN;
  } else if (pending.length > CATCHUP_AT) {
    rate = RATE_CATCHUP;
  } else {
    rate = RATE_NORMAL;
  }

  const toShow = pending.slice(0, rate);
  animState.pending = pending.slice(rate);
  animState.displayed += toShow;

  const ref = streamingRefs.get(animState.msgId);
  if (ref?.current) ref.current.textContent = animState.displayed;

  animState.rafId = requestAnimationFrame(animTick);
}

function enqueueStreamText(text: string) {
  animState.pending += text;
  if (!animState.rafId) animState.rafId = requestAnimationFrame(animTick);
}

function markStreamDone() {
  animState.streamDone = true;
  if (!animState.rafId) animState.rafId = requestAnimationFrame(animTick);
}

function resetStreamAnimation() {
  if (animState.rafId) { cancelAnimationFrame(animState.rafId); animState.rafId = 0; }
  animState.pending = '';
  animState.displayed = '';
  animState.msgId = null;
  animState.streamDone = false;
  animState.started = false;
}

export default function CopilotPanel({ open, onClose, pageContext, screenData, onAction }: CopilotPanelProps) {
  const [messages, setMessages] = useState<CopilotMessage[]>(() => loadMessagesFromStorage().messages);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(() => loadMessagesFromStorage().conversationId);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const activeStreamIdRef = useRef<string | null>(null);

  // Track which message has a status indicator (separate from content to avoid re-renders)
  const [statusMsgId, setStatusMsgId] = useState<string | null>(null);
  const [statusText, setStatusText] = useState('');

  // Session history state
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);

  // Auto-scroll on a 200ms cadence during streaming, immediate on new messages
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const doScroll = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const scheduleScroll = useCallback(() => {
    if (!scrollTimerRef.current) {
      scrollTimerRef.current = setTimeout(() => {
        doScroll();
        scrollTimerRef.current = null;
      }, 150);
    }
  }, [doScroll]);

  useEffect(() => {
    doScroll();
  }, [messages, doScroll]);

  // Restore conversation from localStorage on mount so chat history persists across reloads.
  // The initial state already loads from localStorage (useState initializer), so we just
  // need to ensure the conversationId is restored. If a server-side session is stale,
  // the AI will gracefully start a new context.
  useEffect(() => {
    const stored = loadMessagesFromStorage();
    if (stored.conversationId && !conversationId) {
      setConversationId(stored.conversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync conversationId to sessionStorage
  useEffect(() => {
    if (conversationId) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, conversationId);
    }
  }, [conversationId]);

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    // Don't persist during streaming (only persist completed messages)
    const hasStreaming = messages.some(m => m.streaming);
    if (!hasStreaming) {
      saveMessagesToStorage(messages, conversationId);
    }
  }, [messages, conversationId]);

  // Focus input when panel opens or page context changes (navigation)
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, pageContext]);

  const addMessage = useCallback((role: 'user' | 'assistant', content: string, streaming = false): string => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setMessages(prev => [...prev, { id, role, content, timestamp: new Date(), streaming }]);
    return id;
  }, []);

  // Finalize: let animation drain at accelerated rate, then swap to React state with markdown
  const finalizeMessage = useCallback((id: string, finalContent: string) => {
    markStreamDone(); // animation will drain remaining buffer at RATE_DRAIN
    // Wait for animation to finish, then swap to markdown render
    const checkDone = () => {
      if (animState.rafId && animState.msgId === id) {
        requestAnimationFrame(checkDone);
      } else {
        setMessages(prev =>
          prev.map(m => m.id === id ? { ...m, content: finalContent, streaming: false, statusText: undefined } : m)
        );
        setStatusMsgId(null);
        setStatusText('');
        streamingRefs.delete(id);
      }
    };
    requestAnimationFrame(checkDone);
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ─── Pending-write approve / decline handlers (Privacy Policy §11) ──────
  // Called by the Approve / Decline buttons on a pending_confirmation card.
  // The buttons are disabled after click (state -> 'approving' / 'declining')
  // to prevent a double-click triggering a second consume attempt.

  const handleApprove = useCallback(async (messageId: string, token: string) => {
    // Optimistically mark as approving so the UI disables the button.
    setMessages(prev => prev.map(m =>
      m.id === messageId && m.pendingConfirmation?.token === token
        ? { ...m, pendingConfirmation: { ...m.pendingConfirmation, state: 'approving' } }
        : m
    ));
    try {
      const result = await approvePendingWrite(token);
      setMessages(prev => prev.map(m =>
        m.id === messageId && m.pendingConfirmation?.token === token
          ? { ...m, pendingConfirmation: { ...m.pendingConfirmation!, state: 'approved' } }
          : m
      ));
      // Surface the result to the user. The backend already returned a
      // tool_result to the LLM at /approve time, so the AI's next turn will
      // also confirm success. This user-facing message is for immediate UX.
      void result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessages(prev => prev.map(m =>
        m.id === messageId && m.pendingConfirmation?.token === token
          ? { ...m, pendingConfirmation: { ...m.pendingConfirmation!, state: 'expired', errorMessage: msg } }
          : m
      ));
    }
  }, []);

  const handleDecline = useCallback(async (messageId: string, token: string, reason?: string) => {
    setMessages(prev => prev.map(m =>
      m.id === messageId && m.pendingConfirmation?.token === token
        ? { ...m, pendingConfirmation: { ...m.pendingConfirmation, state: 'declining' } }
        : m
    ));
    try {
      await declinePendingWrite(token, reason);
      setMessages(prev => prev.map(m =>
        m.id === messageId && m.pendingConfirmation?.token === token
          ? { ...m, pendingConfirmation: { ...m.pendingConfirmation!, state: 'declined' } }
          : m
      ));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessages(prev => prev.map(m =>
        m.id === messageId && m.pendingConfirmation?.token === token
          ? { ...m, pendingConfirmation: { ...m.pendingConfirmation!, state: 'expired', errorMessage: msg } }
          : m
      ));
    }
  }, []);

  const processFile = useCallback((file: File) => {
    setAttachedFile(file);
    if (file.name.endsWith('.csv') || file.name.endsWith('.txt') || file.type === 'text/csv') {
      const reader = new FileReader();
      reader.onload = (e) => setFileContent(e.target?.result as string);
      reader.readAsText(file);
    } else {
      setFileContent(null);
    }
  }, []);

  const removeFile = useCallback(() => {
    setAttachedFile(null);
    setFileContent(null);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current++;
    setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current--;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleSend = useCallback(async () => {
    // Check pendingExternalRef first (for programmatic sends from arena etc.)
    const text = (pendingExternalRef.current || input).trim();
    pendingExternalRef.current = null;
    if ((!text && !attachedFile) || isStreaming) return;

    // Build message with optional file content
    let messageText = text;
    if (attachedFile && fileContent) {
      // CSV/text files: include content inline
      const csvLines = fileContent.split('\n').length;
      messageText = text
        ? `${text}\n\n[Attached file: ${attachedFile.name} (${csvLines} lines)]\n\`\`\`csv\n${fileContent.length > 50000 ? fileContent.substring(0, 50000) + '\n... (truncated)' : fileContent}\n\`\`\``
        : `[Attached file: ${attachedFile.name} (${csvLines} lines)]\n\`\`\`csv\n${fileContent.length > 50000 ? fileContent.substring(0, 50000) + '\n... (truncated)' : fileContent}\n\`\`\``;
    } else if (attachedFile) {
      // Non-text files (PDF, XLSX): mention the file but can't read content here
      messageText = text
        ? `${text}\n\n[Attached file: ${attachedFile.name} - ${formatFileSize(attachedFile.size)}]`
        : `[Attached file: ${attachedFile.name} - ${formatFileSize(attachedFile.size)}]`;
    }

    // Clear file attachment
    setAttachedFile(null);
    setFileContent(null);

    setInput('');
    addMessage('user', messageText);
    setIsStreaming(true);

    const assistantId = addMessage('assistant', '', true);
    activeStreamIdRef.current = assistantId;
    let accumulated = '';
    const collectedActions: CopilotAction[] = [];

    // Reset animation state for new message
    resetStreamAnimation();
    animState.msgId = assistantId;

    abortRef.current = new AbortController();

    try {
      await streamChat({
        message: messageText,
        conversationId,
        currentPage: pageContext,
        screenData,
        messageHistory: messages.map(m => ({ role: m.role, content: m.content })),
        onEvent: (event: ChatEvent) => {
          if (event.type === 'text' && event.content) {
            accumulated += event.content;
            // Buffer chars for smooth rAF-driven display
            enqueueStreamText(event.content);
            scheduleScroll();
          }
          // Capture conversation ID from first event to maintain context across messages
          const evtWithConv = event as ChatEvent & { conversationId?: string };
          if (evtWithConv.conversationId && !conversationId) {
            setConversationId(evtWithConv.conversationId);
          }
          if (event.type === 'status' && event.content) {
            setStatusMsgId(assistantId);
            setStatusText(event.content);
          }
          if (event.type === 'tool_use' && event.toolName) {
            const toolLabel = event.toolName.replace(/_/g, ' ');
            setStatusMsgId(assistantId);
            setStatusText(`Using ${toolLabel}...`);
            collectedActions.push({ tool: event.toolName, input: event.input, result: undefined });
          }
          if (event.type === 'tool_result' && event.toolName) {
            const lastAction = collectedActions[collectedActions.length - 1];
            if (lastAction && lastAction.result === undefined) {
              lastAction.result = event.result;
            }
          }
          if (event.type === 'pending_confirmation' && event.confirmationToken) {
            // Write-tool confirmation gate (Privacy Policy §11). The backend
            // captured the original auth context; the UI MUST render Approve /
            // Decline buttons so the user can confirm or cancel before the
            // write executes.
            const expiresInSec = event.expiresInSeconds ?? 300;
            setMessages(prev => prev.map(m => m.id === assistantId ? {
              ...m,
              pendingConfirmation: {
                token: event.confirmationToken!,
                toolName: event.toolName ?? 'unknown',
                summary: event.summary ?? `Confirm ${event.toolName}`,
                expiresAt: Date.now() + expiresInSec * 1000,
                state: 'pending',
              },
            } : m));
            // Stop the "Using tool..." status — we are now waiting for human input.
            setStatusText('Waiting for your confirmation...');
          }
          if (event.type === 'action' && event.action) {
            // Execute navigation/prefill actions IMMEDIATELY when the action event arrives.
            // This gives instant feedback instead of waiting for the stream to complete.
            if (onAction) {
              onAction(event.action);
            }
            // Re-focus input after action dispatch (navigation can steal focus)
            setTimeout(() => inputRef.current?.focus(), 150);
          }
          if (event.type === 'done') {
            // Final render: switch from DOM-ref to React state with markdown
            finalizeMessage(assistantId, accumulated);
          }
        },
        onError: (error: Error) => {
          accumulated += `\n\n[Error: ${error.message}]`;
          finalizeMessage(assistantId, accumulated);
          setIsStreaming(false);
          // Report to external callback (e.g., arena)
          const cb = _completionCallback;
          _completionCallback = null;
          if (cb) cb({ text: accumulated, actions: collectedActions, error: error.message });
          setTimeout(() => inputRef.current?.focus(), 50);
        },
        onComplete: () => {
          finalizeMessage(assistantId, accumulated);
          setIsStreaming(false);
          activeStreamIdRef.current = null;
          // Report to external callback (e.g., arena)
          const cb = _completionCallback;
          _completionCallback = null;
          if (cb) cb({ text: accumulated, actions: collectedActions });
          setTimeout(() => inputRef.current?.focus(), 50);
        },
      });

      const lastEvent = messages;
      void lastEvent;
    } catch (err) {
      if (accumulated) {
        finalizeMessage(assistantId, accumulated + '\n\n[Connection lost]');
      } else {
        finalizeMessage(assistantId, 'Unable to reach AI service. Is the AI Bridge running on port 5291?');
      }
      setIsStreaming(false);
      activeStreamIdRef.current = null;
      // Report to external callback (e.g., arena)
      const cb = _completionCallback;
      _completionCallback = null;
      if (cb) cb({ text: accumulated, actions: collectedActions, error: err instanceof Error ? err.message : 'Connection lost' });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [input, isStreaming, conversationId, addMessage, finalizeMessage, scheduleScroll, messages, onAction, pageContext, screenData, attachedFile, fileContent]);

  // Listen for external sendCopilotMessage() calls
  const pendingExternalRef = useRef<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const message = (e as CustomEvent<string>).detail;
      if (typeof message === 'string' && message.trim() && !isStreaming) {
        const trimmed = message.trim();
        pendingExternalRef.current = trimmed;
        setInput(trimmed);
        // Give React time to commit the state update before triggering send
        setTimeout(() => {
          // Fallback: if handleSend still sees stale input, set textarea directly
          if (inputRef.current && pendingExternalRef.current) {
            inputRef.current.value = pendingExternalRef.current;
          }
          handleSend();
        }, 100);
      }
    };
    window.addEventListener('copilot-send-message', handler);
    return () => window.removeEventListener('copilot-send-message', handler);
  }, [isStreaming, handleSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleClear = () => {
    setMessages([]);
    setConversationId(undefined);
    setAttachedFile(null);
    setFileContent(null);
    localStorage.removeItem(COPILOT_MESSAGES_KEY);
    localStorage.removeItem(COPILOT_CONVERSATION_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  };

  const toggleHistory = useCallback(async () => {
    if (showHistory) {
      setShowHistory(false);
      return;
    }
    setShowHistory(true);
    if (!sessionsLoaded) {
      try {
        const result = await listSessions();
        setSessions(result);
        setSessionsLoaded(true);
      } catch {
        setSessions([]);
      }
    }
  }, [showHistory, sessionsLoaded]);

  const loadSession = useCallback((sessionId: string) => {
    setConversationId(sessionId);
    setMessages([]);
    localStorage.removeItem(COPILOT_MESSAGES_KEY);
    localStorage.removeItem(COPILOT_CONVERSATION_KEY);
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    setShowHistory(false);
    // The next message sent will use this session's context from the server
  }, []);

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    try {
      await deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch {
      // Silently fail — the backend will handle cleanup
    }
  }, []);

  if (!open) return null;

  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200 w-full sm:w-[380px] sm:min-w-[380px] sm:max-w-[380px] fixed inset-0 z-40 sm:static sm:inset-auto sm:z-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <span className="text-sm font-semibold text-gray-900">AI Copilot</span>
          {pageContext && (
            <span className="text-xs text-gray-400 max-w-[160px] truncate" title={pageContext}>
              {pageContext.split(' | ')[0]}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleHistory}
            className={`p-1 rounded hover:bg-gray-200 ${showHistory ? 'text-primary-600 bg-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
            title="Chat history"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          <button
            onClick={handleClear}
            className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
            title="Clear conversation"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
            title="Close copilot"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Session history panel */}
      {showHistory && (
        <div className="border-b border-gray-200 max-h-64 overflow-y-auto bg-gray-50">
          {sessions.length === 0 && !sessionsLoaded && (
            <div className="px-4 py-3 text-xs text-gray-400 text-center">Loading sessions...</div>
          )}
          {sessions.length === 0 && sessionsLoaded && (
            <div className="px-4 py-3 text-xs text-gray-400 text-center">No previous sessions</div>
          )}
          {sessions.map(session => (
            <div
              key={session.id}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
              onClick={() => loadSession(session.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-700 truncate">{session.preview || '(empty session)'}</div>
                <div className="text-[10px] text-gray-400">
                  {new Date(session.lastActivity).toLocaleString('lt-LT')} &middot; {session.messageCount} msg{session.messageCount !== 1 ? 's' : ''}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleDeleteSession(session.id); }}
                title="Delete session"
                className="text-gray-300 hover:text-red-500 flex-shrink-0"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* AI Data Egress Notice — visible before first message, per AI data egress review condition 1 */}
        {messages.length === 0 && (
          <div className="text-center py-8">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <p className="text-sm text-gray-400 mb-2">AI Copilot</p>
            <p className="text-xs text-gray-400">
              Ask accounting questions, get explanations,<br/>or request help with your current task.
            </p>
            {pageContext && (
              <p className="text-xs text-primary-400 mt-2">
                {pageContext.split(' | ')[0]}
              </p>
            )}
            <div className="mt-4 mx-auto max-w-[280px] bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="font-semibold text-amber-800 text-xs">Privacy Notice</span>
              </div>
              <p className="text-[11px] text-amber-700 leading-relaxed">
                Messages are processed by an external AI service (Anthropic). Avoid entering sensitive personal data (national IDs, full bank details, or passwords).
              </p>
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
                msg.role === 'user'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {msg.role === 'assistant' ? (
                msg.streaming ? (
                  // During streaming: DOM-ref driven (no React re-renders per token)
                  <StreamingBubble msgId={msg.id} />
                ) : (
                  // After streaming: full markdown render (one-time)
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content || '') }}
                  />
                )
              ) : (
                msg.content
              )}
              {msg.streaming && !msg.content && !statusText && <TypingDots />}
              {msg.streaming && statusMsgId === msg.id && statusText && (
                <span className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {statusText}
                </span>
              )}
              {msg.streaming && msg.content && !(statusMsgId === msg.id && statusText) && <StreamingCursor />}
              {msg.pendingConfirmation && (
                <PendingConfirmationCard
                  confirmation={msg.pendingConfirmation}
                  onApprove={() => handleApprove(msg.id, msg.pendingConfirmation!.token)}
                  onDecline={() => handleDecline(msg.id, msg.pendingConfirmation!.token)}
                />
              )}
            </div>
          </div>
        ))}
        {/* Thinking indicator while waiting for first response token */}
        {isStreaming && !messages.some(m => m.role === 'assistant' && m.streaming) && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-400 flex items-center gap-1.5">
              <svg className="w-4 h-4 text-primary-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <TypingDots />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 p-3 relative">
        {/* Drop zone overlay */}
        <div
          className={`absolute inset-0 z-10 bg-primary-50 bg-opacity-90 border-2 border-dashed border-primary-400 rounded-b-lg flex items-center justify-center transition-opacity ${isDragOver ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <svg className="w-8 h-8 mx-auto text-primary-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm font-medium text-primary-600">Drop file here</p>
            <p className="text-xs text-primary-400">CSV, PDF, XLSX</p>
          </div>
        </div>
        {attachedFile && (
          <div className="flex items-center gap-2 mb-2 px-2 py-1 bg-gray-50 rounded-lg text-xs text-gray-600">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="truncate max-w-[200px]">{attachedFile.name}</span>
            <span className="text-gray-400">({formatFileSize(attachedFile.size)})</span>
            <button onClick={removeFile} className="ml-auto text-gray-400 hover:text-gray-600" title="Remove">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            placeholder={attachedFile ? `Add a message about ${attachedFile.name}...` : "Ask the AI or drop a file..."}
            rows={1}
            className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 max-h-24"
            disabled={isStreaming}
          />
          <button
            onClick={handleSend}
            disabled={(!input.trim() && !attachedFile) || isStreaming}
            className="flex-shrink-0 p-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Send message"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
            </svg>
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mt-1">
          Enter to send · Shift+Enter for new line · Drop files to attach
          <span className="text-amber-500 ml-1" title="Messages are processed by an external AI service (Anthropic). Do not enter sensitive personal data.">⚠ AI</span>
        </p>
      </div>
    </div>
  );
}
